/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { GrantTokenResponseDto } from '@ink-ai/insight-service-sdk';
import { AppParam } from '../reducers/app';
import { jwtDecode } from 'jwt-decode';

const APP_PARAM_TOKEN = 'INK_AI_APP_PARAM_20250331';
const APP_PARAM_EXPIRES_AT = 'INK_AI_APP_PARAM_EXPIRES_AT';
const DEFAULT_APP_PARAM_TTL_HOURS = 24;

// We use access token for authentication, use expire time in access token by default
export const getExpiresAt = (authBody: GrantTokenResponseDto) => {
  if (!authBody.access_token) {
    return authBody.expires_at;
  }
  const accessToken = jwtDecode(authBody.access_token);
  return accessToken.exp ?? authBody.expires_at;
};

export const getTokens = (): GrantTokenResponseDto => ({
  access_token: localStorage.getItem('accessToken'),
  refresh_token: localStorage.getItem('refreshToken'),
  id_token: localStorage.getItem('idToken'),
  expires_at: parseInt(localStorage.getItem('expiresAt')),
});

export const storeTokens = (grantTokenResp: GrantTokenResponseDto | null) => {
  if (grantTokenResp === null) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('expiresAt');
    return;
  }
  localStorage.setItem('accessToken', grantTokenResp.access_token);
  grantTokenResp.refresh_token &&
    localStorage.setItem('refreshToken', grantTokenResp.refresh_token);
  if (grantTokenResp.id_token) {
    localStorage.setItem('idToken', grantTokenResp.id_token);
  }
  localStorage.setItem('expiresAt', getExpiresAt(grantTokenResp).toString());
};

export const clearAll = () => localStorage.clear();

export const storeAppParam = (appParam: AppParam) => {
  const ttlHours = appParam.app_param_cache_ttl_hours || DEFAULT_APP_PARAM_TTL_HOURS;
  const ttlSeconds = ttlHours * 60 * 60;
  localStorage.setItem(APP_PARAM_TOKEN, JSON.stringify(appParam));
  localStorage.setItem(APP_PARAM_EXPIRES_AT, (Math.floor(Date.now() / 1000) + ttlSeconds).toString());
};

export const getAppParam = (): AppParam | null => {
  const appParamString = localStorage.getItem(APP_PARAM_TOKEN);
  const expiresAt = localStorage.getItem(APP_PARAM_EXPIRES_AT);
  
  if (!appParamString || !expiresAt) {
    return null;
  }
  
  // Check if expired
  if (Math.floor(Date.now() / 1000) > parseInt(expiresAt)) {
    localStorage.removeItem(APP_PARAM_TOKEN);
    localStorage.removeItem(APP_PARAM_EXPIRES_AT);
    return null;
  }
  
  return JSON.parse(appParamString);
};

const LANGUAGE_KEY = 'INK_AI_LANGUAGE';

export const getStoredLanguage = (): string => {
  return localStorage.getItem(LANGUAGE_KEY) || 'en';
};

export const storeLanguage = (language: string) => {
  localStorage.setItem(LANGUAGE_KEY, language);
};
